/*
 * TTTech nerve-management-system-frontend
 * Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 * ALL RIGHTS RESERVED.
 * Usage of this software, including source code, netlists, documentation,
 * is subject to restrictions and conditions of the applicable license
 * agreement with TTTech Industrial Automation AG or its affiliates.
 *
 * All trademarks used are the property of their respective owners.
 * TTTech Industrial Automation AG and its affiliates do not assume any liability
 * arising out of the application or use of any product described or shown
 * herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 * make changes, at any time, in order to improve reliability, function or
 * design.
 *
 * Contact Information:
 * support@tttech-industrial.com
 * TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 */
import { defineStore } from 'pinia';
import { CampaignAndTasksApiService } from '@/services/api';
import CampaignTaskModel from '@/model/campaign-and-tasks/task.model';
import CampaignModel from '@/model/campaign-and-tasks/campaign.model';

const useDeployDetailsStore = defineStore('deployDetails', {
  state: () => ({
    bulkCampaignDetails: {},
    tasks: [],
    deployDetailsCount: 0,
  }),
  actions: {
    setDeployDetailsList(list) {
      this.tasks = list.data.map((deployed) => new CampaignTaskModel({ ...deployed }));
    },

    updatedTask(payload) {
      const task = this.tasks.find((t) => t.id === payload.id);
      if (!task) {
        return;
      }
      task.progress = CampaignTaskModel.formatProgress(payload.progress);
      task.status = CampaignTaskModel.formatStatus(payload?.status);
      if (payload.timeOfTaskStart) {
        task.started = CampaignTaskModel.formatStarted(payload.timeOfTaskStart);
      }
      task.finished = CampaignTaskModel.formatFinished(
        payload?.status,
        payload?.taskFinishedTimestamp,
        payload?.errorFeedback,
      );
      if (payload.errorFeedback) {
        task.errorFeedback = payload.errorFeedback;
      }
    },

    async fetchDeploymentTasks(data) {
      if (data.id) {
        const filterBy = {
          isFinished: data.selectedSuccessful,
          isFailed: data.selectedFailed,
          inProgress: data.selectedInProgress,
        };

        if (data.search) {
          filterBy.device = data.search;
        }

        const params = {
          filterBy,
          limit: data.itemsPerPage || 10,
          page: data.page || 1,
        };

        const list = await CampaignAndTasksApiService.getCampaignTasks({ params }, data.id);
        this.tasks = list.data.map((deployed) => new CampaignTaskModel({ ...deployed }));
        this.deployDetailsCount = list.count;
      }
    },

    async getCampaignAndContentInformation(campId) {
      const campaignInfo = await CampaignAndTasksApiService.getCampaignInformation(campId);
      const campaignContent = await CampaignAndTasksApiService.getCampaignContent(campaignInfo.contentId);
      this.bulkCampaignDetails = new CampaignModel({
        ...campaignInfo,
        contentInfo: { ...campaignContent?.content_params, contentSubType: campaignContent?.content_subtype },
      });
    },

    async markTaskAsFail(data) {
      const task = await CampaignAndTasksApiService.markTaskAsFail(data);
      this.updatedTask(task);
    },

    async markTaskAsSuccess(data) {
      const task = await CampaignAndTasksApiService.markTaskAsSuccess(data);
      this.updatedTask(task);
    },

    clearDeployDetailsListAndCount() {
      this.tasks = [];
      this.deployDetailsCount = 0;
    },

    updateCampaign(payload) {
      if (this.bulkCampaignDetails.id !== payload.id) {
        return;
      }
      this.bulkCampaignDetails.progress = CampaignModel.formatProgress(payload.progress);
      this.bulkCampaignDetails.status = CampaignModel.formatStatus(payload.status);
      // to avoid overwriting information if it is not provided by the event
      if (payload.timeOfOperationStart) {
        this.bulkCampaignDetails.started = CampaignModel.formatDate(payload.timeOfOperationStart);
      }
      // to avoid overwriting information if it is not provided by the event
      if (payload.timeOfOperationFinish) {
        this.bulkCampaignDetails.finished = CampaignModel.formatDate(payload.timeOfOperationFinish);
      }
    },

    handleDeployBomCampaignEvent(payload) {
      const { name, params } = payload;
      const campaignType = params?.campaignType;
      if (campaignType !== 'workload') {
        return;
      }
      if (name === 'all-admins-bom-campaign-updated' || name === 'all-admins-bom-campaign-updated-finished') {
        this.updateCampaign(params);
      }
      if (name === 'all-admins-bom-task-updated') {
        this.updatedTask(params);
      }
    },
  },
});
export default useDeployDetailsStore;
